// Mock data for Thryvin' Landing Page
// These placeholders can be easily updated with real content

export const APP_LINKS = {
  // Thryvin' on the App Store (App ID 6762291224).
  appStore: "https://apps.apple.com/app/id6762291224",
  // Thryvin' on Google Play — LIVE since 2026-08-03.
  playStore: "https://play.google.com/store/apps/details?id=com.thryvin.app"
};

export const HERO_CONTENT = {
  headline: "Your AI coach that keeps you consistent.",
  subheadline: "Workouts that adapt to you. Accountability that sticks. Progress you can see.",
  trustLine: "Trusted by early users"
};

export const PROBLEM_SOLUTION = {
  title: "Why Thryvin'?",
  problem: "Most people struggle with motivation, consistency, and knowing exactly what to do in the gym. Generic plans don't work, and personal trainers cost a fortune.",
  solution: "Thryvin' fixes that with a personalised AI coach that learns your goals, adapts your training, and keeps you accountable — all at a fraction of the cost."
};

export const HOW_IT_WORKS = [
  {
    step: 1,
    title: "Set your goals",
    description: "Tell us what you want to achieve — whether it's building muscle, losing weight, or getting stronger."
  },
  {
    step: 2,
    title: "Get a plan that adapts",
    description: "Your AI coach creates a personalised program that evolves with your progress and schedule."
  },
  {
    step: 3,
    title: "Track, improve, stay accountable",
    description: "Log workouts, get check-ins, and watch your progress unfold with clear insights."
  }
];

export const BENEFITS = [
  {
    id: 1,
    title: "Personalised Programs",
    description: "Training plans built around your goals, experience level, and available equipment.",
    icon: "Target"
  },
  {
    id: 2,
    title: "Built-in Accountability",
    description: "Regular check-ins and reminders that actually keep you on track.",
    icon: "CheckCircle"
  },
  {
    id: 3,
    title: "Saves Money vs a PT",
    description: "Get expert-level guidance without the premium price tag.",
    icon: "Wallet"
  },
  {
    id: 4,
    title: "Beginner-Friendly",
    description: "Clear exercise guidance and video demonstrations for every movement.",
    icon: "Heart"
  },
  {
    id: 5,
    title: "Progress Tracking",
    description: "Visualise your journey with insights that keep you motivated.",
    icon: "TrendingUp"
  },
  {
    id: 6,
    title: "Nutrition Coaching",
    description: "AI-powered meal planning and macro tracking to fuel your goals — coming soon.",
    icon: "Utensils",
    comingSoon: true
  }
];

export const SCREENSHOTS = [
  {
    "id": 1,
    "src": "/images/store-v2/home-dashboard.webp",
    "alt": "Approved store artwork: Training dashboard and today’s workout",
    "title": "Your Training Dashboard",
    "description": "See today's workout and your training progress from home.",
    "position": "right",
    "isPlaceholder": false
  },
  {
    "id": 2,
    "src": "/images/store-v2/weekly-plan.webp",
    "alt": "Approved store artwork: Weekly workout plan",
    "title": "Your Weekly Plan",
    "description": "View your workout schedule and explore exercises for your next session.",
    "position": "left",
    "isPlaceholder": false
  },
  {
    "id": 3,
    "src": "/images/store-v2/active-workout.webp",
    "alt": "Approved store artwork: Active workout with exercise guidance and set logging",
    "title": "Log Your Sets",
    "description": "Record your reps, sets and weights as you train.",
    "position": "right",
    "isPlaceholder": false
  },
  {
    "id": 4,
    "src": "/images/store-v2/coach-chat.webp",
    "alt": "Approved store artwork: AI coach conversation about training",
    "title": "Your AI Coach",
    "description": "Ask your coach about your workouts, exercise guidance and changes to your training.",
    "position": "left",
    "isPlaceholder": false
  },
  {
    "id": 5,
    "src": "/images/store-v2/set-by-set.webp",
    "alt": "Approved store artwork: Rest timer and coaching guidance between sets",
    "title": "Coaching Between Sets",
    "description": "Rest timers and coaching guidance help you work through your session.",
    "position": "right",
    "isPlaceholder": false
  },
  {
    "id": 6,
    "src": "/images/store-v2/badge-journey.webp",
    "alt": "Approved store artwork: Training milestones and badge journey",
    "title": "Training Milestones",
    "description": "Follow your badge journey and celebrate your training milestones.",
    "position": "left",
    "isPlaceholder": false
  },
  {
    "id": 7,
    "src": "/images/store-v2/exercise-guide.webp",
    "alt": "Approved store artwork: Exercise guide with target muscles and form tips",
    "title": "Exercise Guidance",
    "description": "See target muscles, equipment and form guidance for your next exercise.",
    "position": "right",
    "isPlaceholder": false
  }
];

export const TESTIMONIALS = [
  {
    id: 1,
    name: "Sarah M.",
    role: "Busy Professional",
    avatar: "/placeholder-avatar-1.png",
    quote: "I've tried so many fitness apps, but Thryvin' is the first one that actually understands my schedule and keeps me accountable. Down 8kg in 3 months!",
    rating: 5
  },
  {
    id: 2,
    name: "James T.",
    role: "Complete Beginner",
    avatar: "/placeholder-avatar-2.png",
    quote: "As someone who had never stepped foot in a gym, Thryvin' made everything feel achievable. The AI coach explains every exercise perfectly.",
    rating: 5
  },
  {
    id: 3,
    name: "Emma K.",
    role: "Home Workout Enthusiast",
    avatar: "/placeholder-avatar-3.png",
    quote: "Love that it adapts to my home setup. No gym membership needed and I'm seeing real results. The check-ins keep me honest!",
    rating: 5
  }
];

export const FAQS = [
  {
    id: 1,
    question: "Is Thryvin' free to use?",
    answer: "Thryvin' offers a free tier with core features to get you started. For the full AI coaching experience including personalised programs and detailed analytics, check out our Pro subscription."
  },
  {
    id: 2,
    question: "What's included in Pro?",
    answer: "Pro unlocks higher daily AI coaching limits, fully personalised training programs, advanced progress analytics, priority support, and exclusive features as we release them."
  },
  {
    id: 3,
    question: "Do I need gym experience?",
    answer: "Not at all! Thryvin' is designed to be beginner-friendly. Every exercise comes with clear instructions and guidance. Your AI coach will start you at the right level and progress with you."
  },
  {
    id: 4,
    question: "Can I use Thryvin' at home?",
    answer: "Absolutely. Tell us what equipment you have (even if it's just your bodyweight) and we'll create programs that work for your setup — gym, home, or anywhere."
  },
  {
    id: 5,
    question: "How does the AI personalise my plan?",
    answer: "Our AI considers your goals, fitness level, available equipment, schedule, and preferences. As you train, it learns from your progress and feedback to continuously optimise your program."
  },
  {
    id: 6,
    question: "Can I cancel anytime?",
    answer: "Yes, no lock-in contracts. You can cancel your Pro subscription anytime directly from the app. Your data stays safe and you can reactivate whenever you're ready."
  },
  {
    id: 7,
    question: "Is my data safe?",
    answer: "Your privacy matters to us. We use industry-standard encryption and never sell your personal data. You're always in control of your information."
  }
];

export const FOOTER_LINKS = {
  legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms and Conditions", href: "/terms" },
    { label: "Community Guidelines", href: "/community-guidelines" },
    { label: "Child Safety Standards", href: "/child-safety" },
    { label: "Delete Account", href: "/delete-account" }
  ],
  contact: {
    email: "support@thryvin.app",
    support: "/support"
  },
  copyright: `© ${new Date().getFullYear()} Thryvin'. All rights reserved.`
};
